import React, { useState, useEffect } from "react";
import "./HealthcareEligibility.css";

const RAILWAY = process.env.REACT_APP_API_BASE || "";
const MONTH_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// Shown in the confirmation popup when an enrolled person is moved out of the plan.
const OFFBOARD_STEPS = "Please contact Carsten or CJ at Patriot Benefits Group. Tell them either that the employee no longer works here or is no longer eligible for healthcare insurance because they are not working enough hours. They will send that employee a COBRA letter. They will also remove that person from the bill. Please also tell Jessica to remove the healthcare insurance deduction from that employee's paycheck, especially if the employee continues to work with us.";

// "2026-01" -> "Jan ’26"
function shortMonth(key) {
  const [y, mo] = key.split("-");
  return `${MONTH_ABBR[parseInt(mo, 10) - 1]} ’${y.slice(2)}`;
}

export default function HealthcareEligibility() {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [showWaived, setShow]     = useState(false);
  const [showMovedOut, setMoved]  = useState(false);
  const [busyId, setBusyId]       = useState(null);
  const [offboardTarget, setOffboardTarget] = useState(null); // enrolled entry pending confirmation

  const load = (silent) => {
    if (!silent) setLoading(true);
    return fetch(`${RAILWAY}/api/healthcare`)
      .then(r => r.json())
      .then(d => { if (d.error) throw new Error(d.error); setData(d); })
      .catch(e => { if (!silent) setError(e.message); })
      .finally(() => { if (!silent) setLoading(false); });
  };

  useEffect(() => { load(false); }, []);

  // Move a candidate into the Enrolled staff table (or undo). Removes them from
  // candidates immediately, then resyncs from the server so the enrolled list
  // (with computed hours) reflects the change.
  const setEnrolled = async (person, next) => {
    const id = person.id || person.key;
    setBusyId(id);
    if (next) setData(d => ({ ...d, candidates: d.candidates.filter(x => x.id !== id) }));
    else      setData(d => ({ ...d, enrolled: d.enrolled.filter(x => x.key !== id) }));
    try {
      await fetch(`${RAILWAY}/api/healthcare/candidates/${encodeURIComponent(id)}/enroll`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enrolled: next, name: person.name }),
      });
    } finally {
      await load(true);   // resync enrolled + candidates from the server
      setBusyId(null);
    }
  };

  const toggleWaive = async (c) => {
    const next = !c.disinterested;
    setBusyId(c.id);
    setData(d => ({ ...d, candidates: d.candidates.map(x => x.id === c.id ? { ...x, disinterested: next } : x) }));
    try {
      await fetch(`${RAILWAY}/api/healthcare/candidates/${c.id}/disinterest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ disinterested: next, name: c.name }),
      });
    } catch {
      setData(d => ({ ...d, candidates: d.candidates.map(x => x.id === c.id ? { ...x, disinterested: !next } : x) }));
    } finally {
      setBusyId(null);
    }
  };

  const setOffboard = async (e, next) => {
    setBusyId(e.key);
    setData(d => ({ ...d, enrolled: d.enrolled.map(x => x.key === e.key ? { ...x, offboarded: next } : x) }));
    try {
      await fetch(`${RAILWAY}/api/healthcare/enrolled/${encodeURIComponent(e.key)}/offboard`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ offboarded: next, name: e.name }),
      });
    } catch {
      setData(d => ({ ...d, enrolled: d.enrolled.map(x => x.key === e.key ? { ...x, offboarded: !next } : x) }));
    } finally {
      setBusyId(null);
    }
  };

  const confirmOffboard = async () => {
    const e = offboardTarget;
    setOffboardTarget(null);
    if (e) await setOffboard(e, true);
  };

  // Reconcile a Teambridge-vs-portal mismatch by applying the suggested move.
  const resolveDiscrepancy = async (d) => {
    setBusyId("disc:" + d.id);
    try {
      if (d.action === "enroll") {
        await fetch(`${RAILWAY}/api/healthcare/candidates/${encodeURIComponent(d.id)}/enroll`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ enrolled: true, name: d.name }),
        });
      } else if (d.action === "offboard" && d.enrolledKey) {
        await fetch(`${RAILWAY}/api/healthcare/enrolled/${encodeURIComponent(d.enrolledKey)}/offboard`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ offboarded: true, name: d.name }),
        });
      } else if (d.action === "disinterest") {
        await fetch(`${RAILWAY}/api/healthcare/candidates/${encodeURIComponent(d.id)}/disinterest`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ disinterested: true, name: d.name }),
        });
      }
    } finally { await load(true); setBusyId(null); }
  };

  // Dismiss a mismatch (keep the portal as-is). Re-flags if the Teambridge tag changes.
  const dismissDiscrepancy = async (d) => {
    setBusyId("disc:" + d.id);
    try {
      await fetch(`${RAILWAY}/api/healthcare/discrepancies/${encodeURIComponent(d.id)}/ack`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tag: d.teambridgeTag }),
      });
    } finally { await load(true); setBusyId(null); }
  };

  if (loading) return <div className="hc-section"><div className="hc-loading"><span className="spinner" /> Loading healthcare eligibility…</div></div>;
  if (error)   return <div className="hc-section"><div className="hc-error">⚠ {error}</div></div>;
  if (!data)   return null;

  const { months, threshold, enrolled, candidates, links, consecutiveMonthsToQualify } = data;

  const activeEnrolled = enrolled.filter(e => !e.offboarded);
  const movedOut = enrolled.filter(e => e.offboarded);
  const shownEnrolled = showMovedOut ? enrolled : activeEnrolled;

  const activeCandidates = candidates.filter(c => !c.disinterested);
  const waived = candidates.filter(c => c.disinterested);
  const shownCandidates = showWaived ? candidates : activeCandidates;

  const hrsClass = (h) => (h == null ? "" : h >= threshold ? "hc-ok" : "hc-low");

  return (
    <div className="hc-section">
      <div className="hc-head">
        <div>
          <h2 className="hc-title">Healthcare Eligibility &amp; Enrollment</h2>
          <p className="hc-sub">
            Caregivers qualify at <strong>{threshold} hrs/month</strong>. Values are worked hours
            (completed, non-archived shifts) per calendar month.
          </p>
        </div>
        <div className="hc-links">
          <a href={links.ease} target="_blank" rel="noopener noreferrer" className="hc-link ease">Ease ↗</a>
          <a href={links.ibx}  target="_blank" rel="noopener noreferrer" className="hc-link ibx">IBX Portal ↗</a>
        </div>
      </div>

      {/* ── Teambridge discrepancies ── */}
      {data.discrepancies && data.discrepancies.length > 0 && (
        <div className="hc-block hc-discrepancy">
          <div className="hc-block-head">
            <span className="hc-block-title">⚠ Teambridge mismatches <span className="hc-count">({data.discrepancies.length})</span></span>
            <span className="hc-block-note">Where Teambridge&rsquo;s &ldquo;Health Insurance Decision&rdquo; disagrees with the portal. Reconcile or dismiss each.</span>
          </div>
          <div className="hc-disc-list">
            {data.discrepancies.map(d => {
              const busy = busyId === "disc:" + d.id;
              return (
                <div key={d.id} className="hc-disc-row">
                  <div className="hc-disc-who">
                    <span className="hc-disc-name">{d.name}</span>
                    {d.role && <span className={`role-chip role-${d.role.toLowerCase()}`}>{d.role}</span>}
                  </div>
                  <div className="hc-disc-detail">
                    <span className="hc-disc-tag tb">Teambridge: {d.teambridgeTag}</span>
                    <span className="hc-disc-arrow">vs</span>
                    <span className="hc-disc-tag portal">Portal: {d.portalState}</span>
                  </div>
                  <div className="hc-disc-actions">
                    <button className="hc-disc-resolve" disabled={busy} onClick={() => resolveDiscrepancy(d)}>{d.actionLabel}</button>
                    <button className="hc-disc-dismiss" disabled={busy} onClick={() => dismissDiscrepancy(d)}>Dismiss</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Enrolled ── */}
      <div className="hc-block">
        <div className="hc-block-head">
          <span className="hc-block-title">Enrolled staff <span className="hc-count">({activeEnrolled.length})</span></span>
          <span className="hc-block-note">
            Flagged if last month fell below {threshold} hrs.
            {movedOut.length > 0 && (
              <button className="hc-toggle-waived" onClick={() => setMoved(s => !s)}>
                {showMovedOut ? "Hide" : "Show"} {movedOut.length} moved to not interested
              </button>
            )}
          </span>
        </div>
        <div className="hc-table-wrap">
          <table className="hc-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                {months.map(m => <th key={m.key} className="num" title={m.label}>{shortMonth(m.key)}</th>)}
                <th className="hc-act-col"></th>
              </tr>
            </thead>
            <tbody>
              {shownEnrolled.map(e => (
                <tr key={e.key} className={e.offboarded ? "hc-row-waived" : (e.matched && e.compliantPrevMonth === false ? "hc-row-flag" : "")}>
                  <td>
                    <span className="hc-name">{e.name}</span>
                    {e.dependents?.length > 0 && <span className="hc-dep">+{e.dependents.length} dep</span>}
                    {!e.matched && <span className="hc-unmatched" title="No matching Teambridge caregiver — hours can't be verified">unmatched</span>}
                    {e.dynamic && !e.offboarded && <span className="hc-new" title="Enrolled from a candidate in-app">enrolled here</span>}
                    {!e.offboarded && e.matched && e.compliantPrevMonth === false && <span className="hc-warn">below {threshold}</span>}
                    {e.offboarded && <span className="hc-moved">moved out</span>}
                  </td>
                  <td>{e.role && <span className={`role-chip role-${e.role.toLowerCase()}`}>{e.role}</span>}</td>
                  {months.map(m => {
                    const h = e.hoursByMonth?.[m.key];
                    return <td key={m.key} className={`num ${hrsClass(h)}`}>{h == null ? "—" : h}</td>;
                  })}
                  <td className="hc-actions">
                    {e.offboarded ? (
                      <button className="hc-waive" disabled={busyId === e.key} onClick={() => setOffboard(e, false)}>Restore</button>
                    ) : (
                      <>
                        {e.dynamic && (
                          <button className="hc-undo" disabled={busyId === e.key} onClick={() => setEnrolled(e, false)}
                            title="Undo — move back to candidates">Undo</button>
                        )}
                        <button className="hc-move-out" disabled={busyId === e.key} onClick={() => setOffboardTarget(e)}>Move to Not Interested</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Candidates ── */}
      <div className="hc-block">
        <div className="hc-block-head">
          <span className="hc-block-title">
            Enrollment candidates <span className="hc-count">({activeCandidates.length})</span>
          </span>
          <span className="hc-block-note">
            Un-enrolled caregivers at {threshold}+ hrs for {consecutiveMonthsToQualify} consecutive months.
            {waived.length > 0 && (
              <button className="hc-toggle-waived" onClick={() => setShow(s => !s)}>
                {showWaived ? "Hide" : "Show"} {waived.length} waived
              </button>
            )}
          </span>
        </div>

        {shownCandidates.length === 0 ? (
          <div className="hc-empty">No qualifying candidates right now.</div>
        ) : (
          <div className="hc-table-wrap">
            <table className="hc-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  {months.map(m => <th key={m.key} className="num" title={m.label}>{shortMonth(m.key)}</th>)}
                  <th>Contact</th>
                  <th className="hc-act-col"></th>
                </tr>
              </thead>
              <tbody>
                {shownCandidates.map(c => (
                  <tr key={c.id} className={c.disinterested ? "hc-row-waived" : ""}>
                    <td><span className="hc-name">{c.name}</span></td>
                    <td>{c.role && <span className={`role-chip role-${c.role.toLowerCase()}`}>{c.role}</span>}</td>
                    {months.map(m => {
                      const h = c.hoursByMonth?.[m.key];
                      return <td key={m.key} className={`num ${hrsClass(h)}`}>{h == null ? "—" : h}</td>;
                    })}
                    <td>
                      {c.email
                        ? <a href={`mailto:${c.email}`} className="hc-email">{c.email}</a>
                        : <span className="hc-muted">no email on file</span>}
                    </td>
                    <td className="hc-actions">
                      <button className="hc-enroll-btn" disabled={busyId === c.id} onClick={() => setEnrolled(c, true)}
                        title="Move this person into the Enrolled staff table">
                        ✓ Move to Enrolled
                      </button>
                      <button className="hc-waive" disabled={busyId === c.id} onClick={() => toggleWaive(c)}>
                        {c.disinterested ? "Restore" : "Not interested"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Move-to-Not-Interested confirmation ── */}
      {offboardTarget && (
        <div className="hc-modal-backdrop" onClick={() => setOffboardTarget(null)}>
          <div className="hc-modal" onClick={e => e.stopPropagation()}>
            <div className="hc-modal-head">
              <span className="hc-modal-title">Move {offboardTarget.name} out of the plan?</span>
            </div>
            <div className="hc-modal-body">
              <p className="hc-modal-alert">⚠ Before confirming, complete these steps:</p>
              <p className="hc-modal-steps">{OFFBOARD_STEPS}</p>
            </div>
            <div className="hc-modal-actions">
              <button className="hc-modal-cancel" onClick={() => setOffboardTarget(null)}>Cancel</button>
              <button className="hc-modal-confirm" onClick={confirmOffboard}>Move to Not Interested</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
